import React, { useEffect } from "react";
import { CalendarIcon } from "./icons";
import { format, isBefore, isSameDay } from "date-fns";
import { arSA, enUS } from "date-fns/locale";
import { Calendar } from "./ui/calendar";
import { DateRange } from "react-day-picker";
import useSizes from "@/lib/window-sizes-hook";
import FlexiblePopover from "./flexible-popover";
import { useLocale, useTranslations } from "next-intl";
import { FilterX } from "lucide-react";
import { buttonVariants } from "./ui/button";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { setSearchParams } from "@/redux/slices/searchParams.slice";

const RangePicker = () => {
  const q = useSelector((state: RootState) => state.searchParams);
  const isClient = useSelector((state: RootState) => state.isBrowser);
  const dispatch = useDispatch();
  const { maxSm } = useSizes();
  const locale = useLocale();
  const t = useTranslations();
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<DateRange | undefined>(
    undefined
  );

  useEffect(() => {
    if (!q?.from && !q?.to) {
      setSelected(undefined); // Reset selected state when q is cleared
    } else {
      setSelected({
        from: q?.from ? new Date(q?.from) : undefined,
        to: q?.to ? new Date(q?.to) : undefined,
      });
    }
  }, [q.from, q.to]);

  useEffect(() => {
    if (selected?.from && selected?.to) {
      dispatch(
        setSearchParams({
          from: format(selected?.from, "yyyy-MM-dd"),
          to: format(selected?.to, "yyyy-MM-dd"),
        })
      );
    }
  }, [selected]);

  const handleSelect = (range: DateRange | undefined) => {
    if (!range?.from) return;
    if (selected?.from && selected.to) {
      if (isBefore(range?.from, selected.from)) {
        setSelected({
          from: range?.from,
          to: undefined,
        });
      } else
        setSelected({
          from: range?.to,
          to: undefined,
        });
    } else
      setSelected({
        from: range?.from,
        to:
          range?.to && range?.from && !isSameDay(range?.from, range?.to)
            ? range?.to
            : undefined,
      });
  };

  return (
    <FlexiblePopover
      placeholder={t("rangePicker.placeholder")}
      open={open}
      onOpenChange={setOpen}
      fullWidth
      header={
        selected?.from
          ? selected?.to
            ? `${format(selected?.from, "dd LLLL y", {
                locale: locale === "ar" ? arSA : enUS,
              })} - ${format(selected?.to, "dd LLLL y", {
                locale: locale === "ar" ? arSA : enUS,
              })}`
            : format(selected?.from, "dd LLLL y", {
                locale: locale === "ar" ? arSA : enUS,
              })
          : undefined
      }
      customIcon={
        selected?.from ? (
          <FilterX
            className={buttonVariants({
              variant: "destructive",
              size: "icon",
              className: "size-7 rounded-md cursor-pointer",
            })}
            onClick={(e) => {
              e.stopPropagation();
              setSelected(undefined); // Reset local state
              dispatch(setSearchParams({ from: undefined, to: undefined }));
            }}
          />
        ) : (
          <CalendarIcon />
        )
      }
    >
      <Calendar
        locale={locale === "ar" ? arSA : enUS}
        numberOfMonths={isClient && !maxSm ? 2 : 1}
        mode="range"
        initialDate={selected?.from ? new Date(selected.from) : new Date()}
        selected={selected}
        onSelect={handleSelect}
        disabled={{ before: new Date() }}
      />
    </FlexiblePopover>
  );
};

export default RangePicker;
